import { useQueryGetAllOrganizations } from "@apis:organization/organization";
import { useQueryGetAllUsers } from "@apis:user/user";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { useRBACCheck } from "@hooks:useRBAC";
import { Box, CircularProgress, TextField } from "@mui/material";
import { FormProps } from "@pages:settings/Tenent/Bots/BotSpecfications/AISettings/types";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FeatureFlag } from "@:flag";
import { RBACAction, RBACResource } from "@:rbac";

export const General = ({ control, register, formState, type }: FormProps) => {
  const { t } = useTranslation();
  const canManipulateUser = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__users"],
  );

  const { data: organizations, isLoading: organizationsLoading } =
    useQueryGetAllOrganizations();

  const { data: users, isLoading: usersLoading } = useQueryGetAllUsers({
    enabled: canManipulateUser,
  });

  const organizationsOptions = useMemo(() => {
    return (
      organizations?.map((organization) => ({
        value: organization.id,
        label: organization.name,
      })) || []
    );
  }, [organizations]);

  const usersOptions = useMemo(
    () =>
      users?.map((user) => ({
        value: user.id,
        label: user.name,
      })) || [],
    [users],
  );

  return (
    <Box className="Settings__Bot__General">
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <SuperInput.TextField
          required
          label={t("common.name")}
          width="100%"
          {...register("name", {
            required: t("common.requiredField", { field: t("common.name") }),
          })}
          errorText={formState.errors.name?.message}
          sx={{
            flex: 1,
          }}
        />
        <Controller
          name="organization_id"
          rules={{
            required: t("common.requiredField", {
              field: t("tenant.bots.organization"),
            }),
          }}
          control={control}
          render={({ field }) => {
            const selectedValue =
              organizationsOptions.find(
                (option) => option.value === field.value,
              ) ?? null;
            return (
              <SuperInput.Autocomplete
                disabled={type === "update"}
                sx={{
                  flex: 1,
                }}
                clearOnBlur={false}
                loading={organizationsLoading}
                options={organizationsOptions}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                getOptionLabel={(option) => option?.label}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {organizationsLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  );
                }}
                {...field}
                value={selectedValue}
                onChange={(_, { value }) => {
                  field.onChange(value);
                }}
                noOptionsText={t("tenant.noOrganizationFound")}
                required
                width="100%"
                label={t("tenant.bots.organization")}
                errorText={formState.errors.organization_id?.message}
              />
            );
          }}
        />

        {FeatureFlag.UpdateEnabled && (
          <Controller
            name="enabled"
            defaultValue={true}
            control={control}
            render={({ field }) => (
              <SuperInput.Switch
                required
                label={t("common.enabled")}
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        )}
      </Box>

      {canManipulateUser && (
        <Controller
          name="user_ids"
          control={control}
          render={({ field }) => {
            const selectedValue =
              usersOptions.filter((option) =>
                field?.value?.includes(option.value),
              ) ?? [];
            return (
              <SuperInput.Autocomplete
                sx={{
                  flex: 1,
                  marginTop: "1rem",
                }}
                multiple
                clearOnBlur={false}
                loading={usersLoading}
                options={usersOptions}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => option.label}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  );
                }}
                {...field}
                value={selectedValue}
                onChange={(_, value) => {
                  field.onChange(value?.map((item: any) => item.value));
                }}
                noOptionsText={t("tenant.noUserFound")}
                width="100%"
                label={t("tenant.bots.users")}
                errorText={formState.errors.user_ids?.message}
              />
            );
          }}
        />
      )}
    </Box>
  );
};
